import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, User, Zap } from 'lucide-react';
import { PaymentLogosRow } from './PaymentLogos';
import FastByNoFeeHowItWorksModal from './FastByNoFeeHowItWorksModal';
import CustomerBenefitsModal from './CustomerBenefitsModal';

const HeroSection = () => {
 const { t } = useTranslation();
 const [fastOpen, setFastOpen] = useState(false);
 const [benefitsOpen, setBenefitsOpen] = useState(false);
 return (
 <section className="relative bg-black overflow-x-clip pt-32 sm:pt-36 md:pt-28 lg:pt-32 pb-12 lg:pb-20" data-testid="hero-section">
 <div className="pointer-events-none absolute inset-0 z-0">
 <div className="absolute top-0 right-0 w-[55%] h-full bg-gradient-to-l from-amber-500/[0.10] via-amber-600/[0.04] to-transparent" />
 <div className="absolute top-[15%] right-[18%] w-[28rem] h-[28rem] bg-amber-500/[0.10] rounded-full blur-[100px]" />
 <div className="absolute bottom-0 right-1/4 w-[32rem] h-[32rem] bg-amber-600/[0.06] rounded-full blur-3xl" />
 </div>

 <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid grid-cols-1 lg:grid-cols-[5fr_6fr] gap-8 lg:gap-10 xl:gap-12 items-start lg:items-center">
 {/* LEFT — Text + CTAs. iter132 — new problem/solution structure
 requested by the founder:
 (1) Two attention-grabbing pain headlines — commissions + waiting to
 get paid.
 (2) A gold "With NoFee, You Get:" bridge heading.
 (3) Three clean benefit rows (0% commission · one subscription ·
 direct-to-bank payouts).
 (4) A smaller Stripe trust line reinforcing security.
 Goal: within 5 seconds every restaurant owner grasps that NoFee
 removes commissions, ends payout delays, and is one simple monthly
 subscription — payouts secured by Stripe. */}
 <div className="text-left">
 <h1
 className="font-black tracking-tight text-white leading-[1.02]"
 data-testid="hero-headline"
 >
 <span className="block text-[36px] sm:text-[52px] lg:text-[64px] xl:text-[72px]">
 <span className="text-red-500">STOP</span>
 {' '}Paying Platform Commissions of{' '}
 <span className="text-gold-400">Up to 30%</span>.
 </span>
 <span className="block mt-3 sm:mt-4 text-[32px] sm:text-[46px] lg:text-[56px] xl:text-[64px]">
 <span className="text-red-500">STOP</span>
 {' '}Waiting to{' '}
 <span className="text-gold-400">Get Paid</span>.
 </span>
 </h1>

 {/* Solution block — clearly separated from the problem
 headlines with generous top spacing. The bridge heading is
 the ONLY gold accent inside the benefits region so it reads
 like a signpost, not competing with the pain lines above. */}
 <div className="mt-10 sm:mt-12" data-testid="hero-benefits-block">
 <h2
 className="font-['Outfit',_Inter,_sans-serif] font-extrabold tracking-tight text-gold-400 text-[32px] sm:text-[38px] lg:text-[46px] leading-tight"
 data-testid="hero-benefits-heading"
 >
 With NoFee, You Get:
 </h2>

 <ul className="mt-5 sm:mt-6 space-y-3.5 sm:space-y-4" data-testid="hero-benefits-list">
 {[
 '0% Platform Commission',
 'One Simple Monthly Subscription',
 'Get Paid Directly to Your Bank Account',
 ].map((benefit, i) => (
 <li
 key={benefit}
 data-testid={`hero-benefit-${i + 1}`}
 className="flex items-start gap-3.5 text-[18px] sm:text-[20px] lg:text-[21px] text-white/90 font-medium leading-snug"
 >
 <span
 className="mt-[3px] inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gold-400/15 border border-gold-400/40 text-gold-300 shrink-0"
 aria-hidden="true"
 >
 <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
 <path d="M4 10.5l4 4 8-9" />
 </svg>
 </span>
 <span>{benefit}</span>
 </li>
 ))}
 </ul>

 <p
 className="mt-6 sm:mt-7 flex flex-wrap items-center gap-2.5 text-[14px] sm:text-[15px] text-white/60 leading-relaxed max-w-xl"
 data-testid="hero-stripe-trust"
 >
 <PaymentLogosRow logoClass="h-5 sm:h-7 w-auto" />
 <span>
 Payments are securely processed through{' '}
 <strong className="text-white/80 font-semibold">Stripe</strong>{' '}
 and deposited directly into your bank account.
 </span>
 </p>
 </div>

 {/* Two co-equal value-proposition cards: Customers Pay Less + Fast by NoFee.
 Stacked single-column inside the hero's left rail so each card
 gets full width and readable typography on every breakpoint. */}
 <div className="mt-10 grid grid-cols-1 gap-5 max-w-xl" data-testid="hero-audience-boxes">
 <button
 type="button"
 onClick={() => setBenefitsOpen(true)}
 data-testid="hero-customer-box"
 className="group relative text-left rounded-3xl bg-gradient-to-br from-amber-300 via-gold-400 to-amber-500 text-black p-7 sm:p-8 shadow-[0_20px_50px_-15px_rgba(212,175,55,0.45)] hover:shadow-[0_25px_60px_-15px_rgba(212,175,55,0.6)] hover:-translate-y-0.5 transition-all w-full"
 >
 <div className="flex items-start gap-4">
 <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl bg-black/15 backdrop-blur flex items-center justify-center flex-shrink-0">
 <User className="w-6 h-6 sm:w-7 sm:h-7 text-black" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-[34px] sm:text-[40px] lg:text-[44px] font-black leading-[1.05] tracking-tight">
 Pay Less For Your Food.
 </div>
 <p className="mt-3 text-[14.5px] sm:text-[15px] font-medium text-black/80 leading-snug">
 Many restaurants can offer better prices with NoFee because they don't pay high delivery app fees.
 </p>
 <div className="mt-4 flex justify-end">
 <span
 className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-black text-amber-300 text-[12.5px] font-bold group-hover:bg-neutral-900 transition-colors"
 data-testid="hero-customer-learn-more"
 >
 Learn More <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
 </span>
 </div>
 </div>
 </div>
 </button>

 <a
 href="/register?next=onboarding&utm_source=hero_fast"
 data-testid="hero-restaurant-box"
 className="group relative rounded-3xl bg-gradient-to-br from-zinc-900 via-black to-zinc-950 border border-yellow-400/35 hover:border-yellow-400/70 text-white p-7 sm:p-8 transition-all hover:-translate-y-0.5 hover:shadow-[0_25px_60px_-20px_rgba(250,204,21,0.40)] overflow-hidden"
 >
 {/* Soft golden glow */}
 <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 bg-yellow-400/[0.12] rounded-full blur-3xl" />
 <div className="relative flex items-start gap-4">
 <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl border border-yellow-400/40 bg-yellow-400/10 flex items-center justify-center flex-shrink-0">
 <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-400 fill-yellow-400" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="inline-flex items-center gap-1.5 text-white text-[11px] sm:text-[12px] font-black uppercase tracking-[0.18em]">
 <Zap className="w-3 h-3 fill-yellow-400 text-yellow-400" />
 Fast by NoFee
 </div>
 <div className="text-[26px] sm:text-[30px] lg:text-[32px] font-black leading-[1.1] mt-1.5 text-white tracking-tight">
 On-Time Delivery{' '}
 <span className="block sm:inline">Verified</span>
 </div>
 <p className="mt-3 text-[14px] sm:text-[14.5px] text-white/70 leading-snug">
 <strong className="text-white">Verified by real customers.</strong> Restaurants earn trust and visibility by consistently delivering within their promised time.
 </p>
 <button
 type="button"
 onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFastOpen(true); }}
 data-testid="hero-fast-learn-more"
 className="mt-3.5 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-yellow-400/40 hover:border-yellow-400/70 text-[12.5px] font-bold text-yellow-300 hover:text-yellow-200 transition-colors"
 >
 Learn More <ArrowRight className="w-3.5 h-3.5" />
 </button>
 </div>
 </div>
 </a>
 </div>

 {/* Trust line under CTAs — premium conversion microcopy */}
 <p className="mt-5 text-[12px] text-white/45 tracking-wide" data-testid="hero-trust-line">
 {t('hero.trust_line')}
 </p>
 </div>

 {/* RIGHT — Hero image + payment trust box overlay */}
 <div className="relative self-center lg:-mr-2 xl:-mr-6 lg:mt-6 xl:mt-0 lg:-translate-x-[3%] xl:-translate-x-[4%]">
 <picture>
 <source srcSet="/brand/hero_full.webp" type="image/webp" />
 <img
 src="/brand/hero_full.png"
 alt="NoFee restaurant team with dashboard"
 className="block w-full h-auto select-none object-contain scale-[0.92] lg:scale-[0.82] xl:scale-[0.92] origin-top"
 draggable={false}
 data-testid="hero-artwork"
 fetchPriority="high"
 />
 </picture>
 </div>
 </div>
 </div>
 <FastByNoFeeHowItWorksModal
 open={fastOpen}
 onClose={() => setFastOpen(false)}
 ctaSource="hero_restaurant_box"
 />
 <CustomerBenefitsModal
 open={benefitsOpen}
 onClose={() => setBenefitsOpen(false)}
 />
 </section>
 );
};

export default HeroSection;
